import React, { useCallback, useMemo, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import useQuery from 'hooks/useQuery';
import useCategories, { ALL_PRODUCTS_KEY } from 'hooks/useCategories';
import useMediaQuery from 'hooks/useMediaQuery';

import { IGuestProduct } from 'api/guest_sdk';

import { MEDIA_QUERIES } from 'consts';
import { Button, Select, TextFieldSearch, Typography } from 'ui-kit';

import { proxyParams } from 'utils';

import './Filter.scss';

interface Props {
  products: Array<IGuestProduct>;
}

export default function Filter({ products }: Props) {
  const { pathname } = useLocation();
  const history = useHistory();
  const selectRef = useRef(null);
  const params = useQuery<{ category: string; query: string }>();
  const matches = useMediaQuery(MEDIA_QUERIES.xxl);
  const { categories, otherCategories, getOptions } = useCategories({ products });

  const options = getOptions(!matches ? { ...categories, ...otherCategories } : otherCategories);

  const isOptionSelected = useMemo(
    () => options.findIndex(({ value }) => value === params.category) !== -1,
    [params?.category, options.length],
  );
  const defaultOptionValue = matches ? 'More' : 'All products';

  const handleSearchParam = useCallback(proxyParams(params), []);

  const setQueryParams = (search) => {
    history.replace({
      pathname,
      search,
    });
  };

  return (
    <div className="Filter">
      <div className="Filter-content">
        <section className="Filter-content__categories">
          {Object.keys(categories).map((category, index) => (
            <Button
              className={
                (!params.category && category === ALL_PRODUCTS_KEY) || params.category === category
                  ? 'active'
                  : ''
              }
              variant="text"
              color="textGray"
              key={index}
              onClick={() => handleSearchParam({ category }, setQueryParams)}
            >
              {category}
            </Button>
          ))}
          <div ref={selectRef}>
            {!matches && (
              <Typography as="span" variant="body2" color="textGray">
                View:{' '}
              </Typography>
            )}
            {Boolean(options.length) && (
              <Select
                className={(!matches && !params.category) || isOptionSelected ? 'selected' : ''}
                bordered={false}
                options={options}
                value={isOptionSelected ? params.category : defaultOptionValue}
                onChange={(category) => handleSearchParam({ category }, setQueryParams)}
                getPopupContainer={() => selectRef.current}
              />
            )}
          </div>
        </section>
        <section className="Filter-content__search">
          <TextFieldSearch
            placeholder="Search product"
            fullWidth
            initialValue={params?.query}
            onChange={(query) => handleSearchParam({ query }, setQueryParams)}
          />
        </section>
      </div>
    </div>
  );
}
